import { makeStyles, Typography, Link as MuiLink } from '@material-ui/core'
import { Launch } from '@material-ui/icons'
import { GetStaticPaths, GetStaticProps } from 'next'
import React from 'react'

import Markdown from '@aletheia/common/components/Markdown'
import Page from '@aletheia/common/components/Page'
import ScrollToTop from '@aletheia/common/components/ScrollToTop'
import FormattedCurrency from '@aletheia/common/components/__legacy/UI/FormattedCurrency'
import PrettyDate from '@aletheia/common/components/__legacy/utilities/PrettyDate'
import { isEntry } from '@aletheia/common/lib/contentful/types'

import {
  fetchPayoutReport,
  fetchPayoutReports,
  IPayoutReport,
} from '../../contentful'

const useStyles = makeStyles((theme) => ({
  reportDetails: {
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    '& strong': {
      fontFamily: theme.typography.h1.fontFamily,
    },
  },
}))

type PayoutReportSingleProps = {
  PayoutReport: IPayoutReport
}

const PayoutReportSingle: React.FC<PayoutReportSingleProps> = ({
  PayoutReport,
}) => {
  const classes = useStyles()
  const {
    title,
    recipients,
    body,
    amount,
    date,
    fund: Fund,
    eaForumLink,
  } = PayoutReport.fields
  if (!isEntry(Fund)) return null
  const fundTitle = `${Fund.fields.title.replace(/fund$/i, '')} Fund`
  return (
    <Page
      title={title}
      variant="narrow"
      banner={Fund.fields.bannerImage}
      breadcrumbsBase={{
        label: fundTitle,
        href: `/funds/${Fund.fields.slug}`,
      }}
    >
      <div className={classes.reportDetails}>
        <Typography>
          <strong>Payout Date:</strong> <PrettyDate value={date} />
        </Typography>
        <Typography>
          <strong>Total grants:</strong>{' '}
          <FormattedCurrency
            amount={amount}
            currencyCode="USD"
            minimumFractionDigits={0}
            maximumFractionDigits={0}
          />
        </Typography>
        <Typography>
          <strong>Number of grantees:</strong> {recipients.length}
        </Typography>
        {eaForumLink && (
          <Typography>
            <strong>Discussion:</strong>{' '}
            <MuiLink
              href={`${eaForumLink}#comments`}
              target="_blank"
              rel="noopener"
            >
              EA Forum comments <Launch />
            </MuiLink>
          </Typography>
        )}
      </div>
      <Markdown TOC="Contents">{body}</Markdown>
      <ScrollToTop />
    </Page>
  )
}

export default PayoutReportSingle

export const getStaticProps: GetStaticProps<
  PayoutReportSingleProps,
  { slug: string }
> = async ({ params }) => {
  if (!params) throw new Error('Missing params')
  const PayoutReport = await fetchPayoutReport(params.slug)
  if (!PayoutReport)
    return {
      notFound: true,
    }
  return {
    props: { PayoutReport },
    revalidate: 20,
  }
}

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  const PayoutReports = await fetchPayoutReports()
  if (!PayoutReports) throw new Error('Missing Payout Reports')
  return {
    paths: PayoutReports.map((PayoutReport) => ({
      params: { slug: PayoutReport.fields.slug },
    })),
    fallback: 'blocking',
  }
}
